import { useEffect, useRef, useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  isNumericOperator,
  NUMERIC_OPERATORS,
  OPERATOR_LABELS,
  STRING_OPERATORS,
  type FilterOperator,
  type FilterQuery,
  type FilterRule,
} from '@viewer/lib/filter'
import {
  getDimensionDistinctValues,
  type DimensionMode,
} from '@viewer/lib/itemDimensions'
import { DIMENSION_LABELS, newFilterRule } from './filterQuery'

// ---------------------------------------------------------------------------
// Construtor de regras multi-critério, extraído do painel Filtro (Shift+F)
// para poder ser usado noutros sítios sem duplicar linguagem nenhuma.
//
// Porquê extrair em vez de escrever um segundo construtor: a detecção de
// conflitos passou a poder definir cada conjunto por REGRA, e inventar
// uma segunda gramática (outros operadores, outra forma de escolher
// valores) era ensinar duas linguagens para o mesmo trabalho. Aqui vive
// só a UI; o motor continua em `@viewer/lib/filter`.
//
// O painel Shift+F ficou a consumir isto tal e qual: a única diferença é
// que ele já não desenha as linhas de regra, chama o editor.
// ---------------------------------------------------------------------------

export interface FilterQueryEditorProps {
  query: FilterQuery
  onChange: (query: FilterQuery) => void
}

/** Combinador + lista de regras + botão de acrescentar. Sem botões de
 *  acção: quem usa decide o que fazer com a query (seleccionar no 3D,
 *  definir um conjunto de conflitos, ...). */

/** Uma só lista vazia, partilhada. */
const EMPTY_VALUES: string[] = []

export function FilterQueryEditor({ query, onChange }: FilterQueryEditorProps) {
  const updateRule = (id: string, patch: Partial<FilterRule>) => {
    onChange({
      ...query,
      rules: query.rules.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    })
  }
  const addRule = () =>
    onChange({ ...query, rules: [...query.rules, newFilterRule()] })
  const removeRule = (id: string) =>
    onChange({ ...query, rules: query.rules.filter((r) => r.id !== id) })
  const setCombinator = (c: 'and' | 'or') => onChange({ ...query, combinator: c })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
          Combinar
        </span>
        <div className="flex overflow-hidden rounded border border-[var(--color-border)]">
          <button
            type="button"
            onClick={() => setCombinator('and')}
            className={`px-2 py-0.5 text-[10px] font-medium ${
              query.combinator === 'and'
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)]'
            }`}
          >
            AND
          </button>
          <button
            type="button"
            onClick={() => setCombinator('or')}
            className={`border-l border-[var(--color-border)] px-2 py-0.5 text-[10px] font-medium ${
              query.combinator === 'or'
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)]'
            }`}
          >
            OR
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {query.rules.map((rule) => (
          <FilterRuleRow
            key={rule.id}
            rule={rule}
            onChange={(patch) => updateRule(rule.id, patch)}
            onRemove={() => removeRule(rule.id)}
            canRemove={query.rules.length > 1}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={addRule}
        className="flex items-center gap-1 self-start rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
      >
        <Plus size={10} /> Regra
      </button>
    </div>
  )
}

export interface FilterRuleRowProps {
  rule: FilterRule
  onChange: (patch: Partial<FilterRule>) => void
  onRemove: () => void
  canRemove: boolean
}

export function FilterRuleRow({
  rule,
  onChange,
  onRemove,
  canRemove,
}: FilterRuleRowProps) {
  const models = useAppStore((s) => s.models)
  const isProperty = rule.dimension === 'property'
  const operators = isProperty ? NUMERIC_OPERATORS : STRING_OPERATORS

  // Distinct values for the dropdown, recomputed when dimension or
  // propertyKey changes (and on first mount).
  // Os valores guardam consigo o critério de que vieram: assim ficam
  // velhos sozinhos quando a dimensão muda, e não é preciso marcar "a
  // carregar" no corpo do efeito, que encadeia renders.
  const criterioActual = `${rule.dimension}|${rule.propertyKey ?? ''}`
  const [carga, setCarga] = useState<{ criterio: string; values: string[] }>({
    criterio: '',
    values: [],
  })
  const values = carga.criterio === criterioActual ? carga.values : EMPTY_VALUES
  const valuesLoading = carga.criterio !== criterioActual

  useEffect(() => {
    let cancelled = false
    const fragmentsModels = models
      .map((m) => viewerApi.getModel(m.id))
      .filter((m): m is NonNullable<typeof m> => m != null)
    const criterion =
      rule.dimension === 'property'
        ? {
            mode: 'property' as const,
            propertyKey: rule.propertyKey ?? '',
          }
        : { mode: rule.dimension }
    void getDimensionDistinctValues(fragmentsModels, criterion)
      .then((vs) => {
        if (cancelled) return
        setCarga({ criterio: criterioActual, values: vs })
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('[filter] getDimensionDistinctValues failed', err)
        // Marcar como carregado à mesma: senão fica "a carregar" para
        // sempre sobre uma dimensão que não se consegue ler.
        setCarga({ criterio: criterioActual, values: [] })
      })
    return () => {
      cancelled = true
    }
  }, [rule.dimension, rule.propertyKey, models, criterioActual])

  // Snap operator to a valid one when switching dimension.
  // Dependência propositadamente limitada a `rule.dimension`: só a troca
  // de dimensão deve forçar o operador. Incluir `rule.operator`/`operators`
  // faria o efeito correr a cada escolha manual de operador e `onChange`
  // é recriado a cada render do pai, o que geraria um ciclo de renders.
  useEffect(() => {
    if (!operators.includes(rule.operator)) {
      onChange({ operator: operators[0] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule.dimension])

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] p-1.5">
      {/* Dimension */}
      <select
        value={rule.dimension}
        onChange={(e) =>
          onChange({
            dimension: e.target.value as DimensionMode,
            values: [],
            propertyKey: e.target.value === 'property' ? '' : undefined,
          })
        }
        aria-label="Critério da regra"
        className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5 text-[11px]"
      >
        {(Object.keys(DIMENSION_LABELS) as DimensionMode[]).map((d) => (
          <option key={d} value={d}>
            {DIMENSION_LABELS[d]}
          </option>
        ))}
      </select>

      {/* Property key (only when dimension === 'property') */}
      {isProperty && (
        <input
          type="text"
          value={rule.propertyKey ?? ''}
          onChange={(e) => onChange({ propertyKey: e.target.value })}
          placeholder="Pset_X.Y ou Name"
          aria-label="Propriedade"
          className="w-[140px] rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5 text-[11px]"
        />
      )}

      {/* Operator */}
      <select
        value={rule.operator}
        onChange={(e) =>
          onChange({ operator: e.target.value as FilterOperator })
        }
        aria-label="Operador"
        className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5 text-[11px]"
      >
        {operators.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>

      {/* Value picker: numeric operators take one number; string operators
          take a list of values (OR within the rule). */}
      {isNumericOperator(rule.operator) ? (
        <input
          type="number"
          value={rule.values[0] ?? ''}
          onChange={(e) =>
            onChange({ values: e.target.value === '' ? [] : [e.target.value] })
          }
          placeholder="número"
          aria-label="Valor"
          className="w-[80px] rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5 text-[11px]"
        />
      ) : (
        <MultiValuePicker
          ruleId={rule.id}
          selected={rule.values}
          suggestions={values}
          loading={valuesLoading}
          onChange={(vs) => onChange({ values: vs })}
        />
      )}

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="rounded px-1 py-0.5 text-[var(--color-text-muted)] hover:text-red-500"
          title="Remover regra"
        >
          <Trash2 size={11} />
        </button>
      )}
    </div>
  )
}

export interface MultiValuePickerProps {
  ruleId: string
  selected: string[]
  suggestions: string[]
  loading: boolean
  onChange: (values: string[]) => void
}

/** Pill-tag input for picking multiple discrete values. Type to filter
 *  the suggestion list, click a suggestion (or Enter on the input) to
 *  add it as a pill. Click X on a pill (or Backspace on empty input)
 *  to remove. */
export function MultiValuePicker({
  ruleId,
  selected,
  suggestions,
  loading,
  onChange,
}: MultiValuePickerProps) {
  const [input, setInput] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const add = (raw: string) => {
    const v = raw.trim()
    if (!v) return
    if (selected.includes(v)) return
    onChange([...selected, v])
    setInput('')
  }
  const remove = (v: string) => onChange(selected.filter((x) => x !== v))

  // Filter suggestions by input + exclude already-selected.
  const lower = input.toLowerCase()
  const filtered = suggestions
    .filter((s) => !selected.includes(s))
    .filter((s) => lower === '' || s.toLowerCase().includes(lower))
    .slice(0, 60)

  return (
    <div
      className="relative flex min-w-[160px] flex-1 flex-wrap items-center gap-0.5 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5"
      onClick={() => inputRef.current?.focus()}
    >
      {selected.map((v) => (
        <span
          key={v}
          className="flex items-center gap-1 rounded bg-[var(--color-accent-bg)] px-1.5 py-0.5 text-[10px] text-[var(--color-accent)]"
        >
          {v}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              remove(v)
            }}
            className="text-[var(--color-accent)] hover:text-red-500"
            aria-label={`Remover ${v}`}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => {
          setInput(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        // Delay close so a click on a suggestion still fires. While
        // we're at it: auto-commit the typed text as a pill so the user
        // doesn't have to remember to press Enter before clicking
        // Aplicar. The 150ms window also gives suggestion mouse-down
        // time to consume the input first (in which case `input` will
        // already be empty by the time we run).
        onBlur={() =>
          setTimeout(() => {
            setOpen(false)
            const pending = input.trim()
            if (pending !== '' && !selected.includes(pending)) {
              add(pending)
            }
          }, 150)
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            // If the input matches a suggestion exactly, prefer it;
            // otherwise add as free-text.
            const exact = suggestions.find(
              (s) => s.toLowerCase() === input.toLowerCase(),
            )
            add(exact ?? input)
          } else if (
            e.key === 'Backspace' &&
            input === '' &&
            selected.length > 0
          ) {
            remove(selected[selected.length - 1])
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
        placeholder={
          selected.length === 0
            ? loading
              ? '(a carregar…)'
              : 'escolhe ou escreve…'
            : '+ adicionar'
        }
        className="min-w-[60px] flex-1 bg-transparent px-0.5 py-0 text-[11px] outline-none"
      />
      {open && filtered.length > 0 && (
        <ul
          id={`vn2r-filter-suggestions-${ruleId}`}
          className="absolute top-[calc(100%+2px)] left-0 z-[900] max-h-48 min-w-full overflow-auto rounded border border-[var(--color-border)] bg-[var(--color-surface)] py-0.5 shadow-2xl"
        >
          {filtered.map((s) => (
            <li
              key={s}
              onMouseDown={(e) => {
                // mouseDown fires before input blur, so the suggestion
                // commits even though the input is losing focus.
                e.preventDefault()
                add(s)
              }}
              className="cursor-pointer px-2 py-0.5 text-[11px] text-[var(--color-text)] hover:bg-[var(--color-accent-bg)] hover:text-[var(--color-accent)]"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default FilterQueryEditor
